import React, { useEffect, useState } from 'react';
import DropdownOrder from '../../../../Store/DropdownOrder.jsx';
import DropdownTipoVista from '../../../../Store/DropdownTipoVista.jsx';
import BuscadorInput from '../../../../Store/BuscadorInput.jsx';
import classNames from 'classnames';
import axios from 'axios';
import { Box, Boxes, Calendar, CalendarClock, Eye, Link2, PlusCircle } from 'lucide-react';
import Cookies from 'js-cookie';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useEditor } from '../../../../../App.jsx';
import TokenCard from './TokenCard.jsx';
import { toast } from 'react-toastify';
import { getThemeContext } from '../../../../../components/providers/ThemeProvider.jsx';

const arrayDatosOrdenacion = [
    { nombre: 'Token',             valor: 'token',       icon: <Box       className="w-4 h-4 mr-2" /> },
    { nombre: 'Fecha de creación', valor: 'created_at',  icon: <Calendar  className="w-4 h-4 mr-2" /> },
    // Caducidad ascendente = "qué se me caduca ya", que es la pregunta que se
    // le hace a esta pantalla. Usos, para ver qué enlace se está abriendo de
    // verdad. Modelo, para juntar los enlaces de una misma pieza.
    { nombre: 'Fecha de caducidad',valor: 'expires_at',  icon: <CalendarClock className="w-4 h-4 mr-2" /> },
    { nombre: 'Nº de usos',        valor: 'usage_count', icon: <Eye       className="w-4 h-4 mr-2" /> },
    { nombre: 'Modelo',            valor: 'model_3d',    icon: <Boxes     className="w-4 h-4 mr-2" /> },
];

const initialState = {
    mode: 'grid',
    data: { columns: 3 },
    sortBy: 'created_at',
    sortDirection: 'asc',
};

// El estado lo decide el backend con el mismo criterio que `validate_token`,
// para que "vigente" aquí signifique lo mismo que al abrir el enlace.
const STATUS_FILTERS = [
    { value: 'all',       label: 'Todos' },
    { value: 'active',    label: 'Vigentes' },
    { value: 'expired',   label: 'Caducados' },
    { value: 'exhausted', label: 'Sin usos' },
];

const EnlacesPublicosAdmin = () => {
    const [tokens, setTokens]   = useState([]);
    const [loading, setLoading] = useState(false);
    const theme = getThemeContext();

    const { setStateEditor } = useEditor();

    const handleAddToken = () => {
        setStateEditor(prev => ({ ...prev, editor: true, editorID: 'add', editorType: 'public_token_input' }));
    };

    const [viewMode, setViewMode] = useState(() => {
        const saved = Cookies.get('viewModeTokens');
        return saved ? JSON.parse(saved) : initialState;
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchTokens = () => {
        setLoading(true);
        axios.get('public-tokens/', {
            params: {
                sortBy: viewMode.sortBy,
                sortDirection: viewMode.sortDirection,
                search: searchTerm,
                status: statusFilter === 'all' ? undefined : statusFilter,
            },
        })
            .then(res => setTokens(res.data))
            .catch(err => console.error('Error fetching tokens:', err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTokens();
        Cookies.set('viewModeTokens', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode, searchTerm, statusFilter]);

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const onDeleteToken = async (tokenObject) => {
        try {
            const res = await axios.delete(`/public-tokens/${tokenObject.id}/`);
            if (res.status === 204) {
                setTokens(prev => prev.filter(t => t.id !== tokenObject.id));
                toast.success(`Token eliminado`, { theme: theme === 'dark' ? 'dark' : 'light' });
            }
        } catch {
            toast.error('Error al eliminar el token', { theme: theme === 'dark' ? 'dark' : 'light' });
        }
    };

    /**
     * Alarga la caducidad sin recargar la lista entera.
     *
     * La fecha nueva la calcula el backend (cuenta desde hoy si el enlace ya
     * había caducado), así que se guarda lo que responde en vez de adivinarla
     * aquí. Si el filtro activo es "Caducados", el enlace revivido deja de
     * pertenecer a la lista: por eso ahí sí se recarga.
     */
    const onExtendToken = async (tokenObject, days) => {
        try {
            const { data } = await axios.post(`/public-tokens/${tokenObject.id}/extend/`, { days });
            if (statusFilter === 'expired' || statusFilter === 'exhausted') {
                fetchTokens();
            } else {
                setTokens(prev => prev.map(t => (t.id === data.id ? data : t)));
            }
            toast.success(`Caducidad ampliada ${days} días`, { theme: theme === 'dark' ? 'dark' : 'light' });
        } catch (error) {
            toast.error(
                error?.response?.data?.detail || 'No se pudo ampliar la caducidad',
                { theme: theme === 'dark' ? 'dark' : 'light' },
            );
        }
    };

    const onEditToken = (tokenObject) => {
        setStateEditor(prev => ({ ...prev, editor: true, editorID: tokenObject.id, editorType: 'public_token_input' }));
    };

    const handleCopyURLToken = (token) => {
        const url = `${window.location.origin}/${token.url_token}`;
        navigator.clipboard.writeText(url)
            .then(() => toast.success('URL copiada al portapapeles', { theme: theme === 'dark' ? 'dark' : 'light' }))
            .catch(() => toast.error('Error al copiar la URL', { theme: theme === 'dark' ? 'dark' : 'light' }));
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Toolbar */}
            <div className="glassCard rounded-xl p-3 z-40 flex flex-wrap gap-2 items-center justify-between">
                <button onClick={handleAddToken} className="btn btn-sm btn-primary gap-1">
                    <PlusIcon className="w-4 h-4" /> Añadir enlace
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    <DropdownOrder
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        arrayDatosOrdenacion={arrayDatosOrdenacion}
                    />
                    <DropdownTipoVista
                        options={[
                            { label: '2 columnas', value: { mode: 'grid', columns: 2 } },
                            { label: '3 columnas', value: { mode: 'grid', columns: 3 } },
                            { label: '4 columnas', value: { mode: 'grid', columns: 4 } },
                            { label: 'Lista',      value: { mode: 'list' } },
                        ]}
                        selected={viewMode.mode}
                        selectedValue={viewMode}
                        onChange={val => setViewMode(prev => ({ ...prev, mode: val.mode, columns: val.columns }))}
                    />
                    <BuscadorInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>
            </div>

            {/* Filtro por estado + recuento de lo que se está viendo */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <div role="tablist" className="tabs tabs-boxed tabs-sm">
                    {STATUS_FILTERS.map(filter => (
                        <button
                            key={filter.value}
                            role="tab"
                            className={classNames('tab', { 'tab-active': statusFilter === filter.value })}
                            onClick={() => setStatusFilter(filter.value)}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
                {!loading && (
                    <span className="text-xs text-base-content/60">
                        {tokens.length} {tokens.length === 1 ? 'enlace' : 'enlaces'}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className={classNames(
                'h-full flex-1 overflow-y-auto scrollbar_mod glassCard rounded-xl p-3',
                { 'flex items-center justify-center': !loading && tokens.length === 0 }
            )}>
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                ) : tokens.length > 0 ? (
                    viewMode.mode === 'grid' ? (
                        <div className={classNames(
                            `grid gap-4`,
                            `grid-cols-${viewMode?.columns} md:grid-cols-${viewMode?.columns} lg:grid-cols-${viewMode?.columns}`
                        )}>
                            {tokens.map((token, index) => (
                                <TokenCard
                                    key={token.id || index}
                                    tokenData={token}
                                    formatDate={formatDate}
                                    onDelete={onDeleteToken}
                                    onCopy={handleCopyURLToken}
                                    onEdit={onEditToken}
                                    onExtend={onExtendToken}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {tokens.map((token, index) => (
                                <TokenCard
                                    key={token.id || index}
                                    tokenData={token}
                                    formatDate={formatDate}
                                    onDelete={onDeleteToken}
                                    onCopy={handleCopyURLToken}
                                    onEdit={onEditToken}
                                    onExtend={onExtendToken}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                        <Link2 size={40} className="text-base-content/20" />
                        <p className="text-base-content/50">No hay enlaces públicos disponibles.</p>
                        <p className="text-sm text-base-content/40">
                            Crea un enlace para controlar el acceso a tus modelos 3D.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnlacesPublicosAdmin;
